/**
 * How big an on-screen sheet prints on A4.
 *
 * The desks preview a receipt, a shift report or a day's table in a modal and
 * press print. They expect the paper to look like the preview, on one page.
 * Printing the app's own page could do neither: the sheet reflowed to the
 * paper's width, so the layout changed, and a table a little taller than the
 * page spilled a few rows onto a second sheet.
 *
 * So the sheet is laid out at the width it was previewed at and scaled as a
 * whole to fit the printable area, the way "fit to page" works in a PDF
 * viewer. A sheet so long that one page would make it unreadable (a whole
 * medical file, a day of files) is scaled to the page width only and flows on
 * to as many pages as it needs.
 *
 * Pure numbers: no DOM. services/printSheet.js does the measuring.
 */

/** CSS pixels per millimetre (CSS fixes 96 px to the inch). */
export const PX_PER_MM = 96 / 25.4;

export const A4_MM = Object.freeze({
  portrait: Object.freeze({ width: 210, height: 297 }),
  landscape: Object.freeze({ width: 297, height: 210 }),
});

/** The paper margin, every side. */
export const PAGE_MARGIN_MM = 8;

/**
 * Below this the text is too small to read on paper, so the sheet stops
 * shrinking to fit one page and flows onto more pages instead.
 */
export const MIN_ONE_PAGE_SCALE = 0.5;

/**
 * A fraction of a pixel of rounding is enough for the browser to start a
 * second, empty page. Fit to slightly less than the exact page.
 */
const SAFETY = 0.985;

/** The printable box of an A4 page, in CSS pixels. */
export function printableBox(orientation = 'portrait', marginMm = PAGE_MARGIN_MM) {
  const page = A4_MM[orientation] || A4_MM.portrait;
  return {
    width: Math.floor((page.width - 2 * marginMm) * PX_PER_MM),
    height: Math.floor((page.height - 2 * marginMm) * PX_PER_MM),
  };
}

/**
 * The scale for a sheet of `size` (CSS px) on one orientation.
 *
 * fit 'page'  — the whole sheet on one page when that stays readable;
 * fit 'width' — the page width only, flowing onto more pages.
 *
 * Never enlarges: a small receipt prints at its own size.
 */
export function fitScale(size, orientation = 'portrait', { fit = 'page', minScale = MIN_ONE_PAGE_SCALE } = {}) {
  const box = printableBox(orientation);
  const width = Math.max(1, Number(size?.width) || 1);
  const height = Math.max(1, Number(size?.height) || 1);
  const byWidth = Math.min(1, box.width / width);
  const byPage = Math.min(byWidth, box.height / height);
  if (fit === 'page' && byPage >= minScale) {
    return { scale: round(byPage === 1 ? 1 : byPage * SAFETY), onePage: true, orientation };
  }
  return { scale: round(byWidth === 1 ? 1 : byWidth * SAFETY), onePage: byWidth * height <= box.height, orientation };
}

/**
 * The orientation and scale to print at.
 *
 * 'auto' keeps A4 portrait unless the sheet is wide enough that landscape
 * prints it noticeably larger (a wide table) — and never picks landscape for
 * a sheet that only fits one page in portrait anyway at full size.
 */
export function planPrint(size, { orientation = 'auto', fit = 'page' } = {}) {
  if (orientation === 'portrait' || orientation === 'landscape') return fitScale(size, orientation, { fit });
  const portrait = fitScale(size, 'portrait', { fit });
  const landscape = fitScale(size, 'landscape', { fit });
  if (portrait.scale === 1 && portrait.onePage) return portrait;
  const portraitBetter = portrait.onePage && !landscape.onePage;
  const landscapeBetter = landscape.onePage && !portrait.onePage;
  if (landscapeBetter) return landscape;
  if (portraitBetter) return portrait;
  return landscape.scale > portrait.scale * 1.15 ? landscape : portrait;
}

const round = (n) => Math.round(n * 1000) / 1000;
